// Product brief for CCAH / Continuing Care at Home membership programs —
// Fortified Life (Western Home) and Stay Your Way. See cmTwo.ts for the format
// intent (mechanics a reviewer can't see from numbers alone + things to eyeball).

export const membershipProgramBrief = `**CCAH Membership Programs** — Fortified Life (Western Home) and Stay Your Way.
Continuing Care at Home service/membership agreements modeled as a single-pool
reimbursement LTC product. \`calculationType\` is \`longTermCareInsurance\` but they
route via \`isMembershipProgram\`, NOT the bare traditional-LTC path, because their
eligibility mechanics differ.

Pool model — **single uncapped pool, no death benefit**:
- The real product defines **no lifetime dollar pool** — Fortified Life provides
  Services "at Fortified Life's cost" for the life of the Member. So membership
  programs are modeled as **UNCAPPED**: when \`policyMaximumBenefitAmount\` is
  null/undefined the \`base_pool\` is seeded to the unlimited sentinel
  (999999999) and \`terms.isUnlimited === true\`, exactly like legacy
  \`handleUnlimitedBenefit\`. The pool NEVER binds — only the monthly cap and care
  duration bound payout, so the \`pool-exhausted\` milestone never fires.
- A populated \`policyMaximumBenefitAmount\` (planner-supplied finite pool) is
  honored as a real cap and CAN exhaust — but that is a modeling assumption with
  no contractual basis (the contract has no pool).
- \`eob\` (second pool) is $0 on every row. \`db\` (death benefit) is $0 on every
  row; \`finalizeDeathBenefit → 0\`. On death the agreement just terminates (only
  an Entrance-Fee refund may remain — a surrender return, unmodeled).

Eligibility — **immediate, no gates**:
- **NO 2-ADL trigger.** Care is benefit-eligible from day 1 regardless of ADL
  count; \`non2AdlDays === 0\` on every row, \`eligibleDays === 30\`. The \`lt-2-adl\`
  ineligibility reason never fires. KNOWN QUIRK: the Fortified Life contract
  actually gates Home Site Services on >= 1 ADL deficiency (§3.4), but we model
  0-ADL (all eligible), matching legacy — a small over-eligibility vs. the
  contract, far closer than the trad-LTC 2-ADL gate. We have no 1-ADL primitive.
- **NO waiting / elimination period.** All care days eligible immediately;
  \`waitingPeriod\` is seeded to { home: 0, facility: 0 } and never decrements.
  \`status.epSatisfied === true\` from month 0; the \`in-wp\` reason never fires.

Payout model — reimbursement (default) or cash indemnity:
- **Reimbursement** (default): pays min(eligible cost, monthly cap) from the pool.
  Care cost above the cap is out-of-pocket.
- **Indemnity** (if \`isIndemnityPolicyPayment\`): pays min(cap, pool) as cash.

Inflation — **none on the benefit side**:
- These products have NO benefit inflation rider — services are provided in-kind
  at cost, so there is nothing to inflate. Model at \`policyInflationProtection = 0\`;
  the inflation code path is harmless dead code at 0%. (The 4%/yr cap on the
  Monthly Maintenance Fee is a *fee* escalation — cost-side, outside the engine.)

Premiums — **cost-side only, NOT modeled here**:
- Monthly Maintenance Fees continue for the life of the Member (through care),
  and legacy extends the premium-investment period to end-of-LTC
  (\`premiumInvestmentEndYear = calcLtcEndYear(...)\`). This lives entirely in
  \`calcPolicyTotalCost\`, keyed off \`isMembershipProgram\` — the projection engine
  does not model premiums and this strategy does not reproduce it.

Ground truth — sample contract (\`Fortified Life Contract.pdf\`, Rev. 2020-04,
Western Home Services, Iowa):
- This is a **membership/service agreement, NOT an LTC illustration**. It contains
  **NO concrete benefit figures** — no Coverage Maximum, no Daily/Monthly Benefit
  Maximum, no inflation rate, no premium schedule. The only verified-in-contract
  facts are structural: no benefit pool (§3, p.4 "Provide" = services at cost),
  no waiting period (§2.8 services begin on Membership Date), no benefit-period
  cap (Term = life of Member, §8.1), no death benefit (§8.1, only an Entrance-Fee
  refund per Exhibit C), and a 1-ADL Home-Site gate (§3.4) we approximate as
  0-ADL. There are no carrier dollar amounts to reconcile against — any pool /
  daily-benefit values in a projection are planner-supplied assumptions, not
  transcribed carrier terms. No \`stayYourWay\` document exists; the two are
  assumed mechanically identical (unverified).

Things to eyeball:
- \`eob\` and \`db\` are $0 on every row — single pool, no death benefit.
- \`non2AdlDays === 0\` and \`eligibleDays === 30\` on every row; first paying month
  is month 0 (no EP, no ADL delay), even when ADL count / WP-days are populated.
- For an uncapped program (\`isUnlimited === true\`) the pool never exhausts — no
  \`pool-exhausted\` milestone; out-of-pocket appears only when care cost exceeds
  the monthly cap.
- The running \`base_pool\` balance under \`isUnlimited\` sits just below the ~$1B
  sentinel and is NOT meaningful — the UI renders it as "Unlimited".
`;
